import { useEffect, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, CaretDown, Check, FileArrowUp } from "@phosphor-icons/react";
import { Redirect, useLocation } from "wouter";
import { RESUME_ACCEPT, type ProfileView } from "../../../shared/types.ts";
import { Button, Card, Logo, MarkLoader, Steps, cn, ease, softSpring } from "../../ui/index.ts";
import { errorText, getFeed, saveProfile, type ProfilePatch } from "../../lib/api.ts";
import { confettiFrom } from "../../lib/confetti.ts";
import { useApp } from "../../lib/ctx.ts";
import { num } from "../../lib/format.ts";
import { pickedPlan } from "../../lib/plan.ts";
import { getSession, uploadResume } from "../../lib/supabase.ts";
import { AnswerFields, ContactFields, EducationFields, PrefFields } from "../ProfileFields.tsx";
import { Notice } from "../settings/parts.tsx";
import { applyFound, findContact, fileText } from "./prefill.ts";

/** Set when the user leaves setup for now, so the app stops sending them back to it until the tab closes. */
export const SKIP_KEY = "headcount.welcome.skipped";

const NEEDS: Record<string, string> = { firstName: "first name", lastName: "last name", email: "email", phone: "phone", resume: "resume" };

interface StepDef {
  label: string;
  title: string;
  lede: string;
  patch: (p: ProfileView) => ProfilePatch;
}

const STEPS: StepDef[] = [
  {
    label: "You",
    title: "Start with your resume.",
    lede: "Headcount attaches it to every application and fills in your details from it.",
    patch: (p) => ({
      firstName: p.firstName.trim(),
      lastName: p.lastName.trim(),
      email: p.email.trim(),
      phone: p.phone.trim(),
      pronouns: p.pronouns?.trim(),
      address: { city: p.address?.city?.trim(), state: p.address?.state?.trim(), postal: p.address?.postal?.trim() },
      links: { linkedin: p.links.linkedin?.trim(), github: p.links.github?.trim(), website: p.links.website?.trim() },
      education: p.education.filter((e) => e.school.trim()).map((e) => ({ ...e, school: e.school.trim() })),
    }),
  },
  {
    label: "Jobs you want",
    title: "What jobs do you want?",
    lede: "Headcount ranks every open role against this.",
    patch: (p) => ({
      prefs: {
        families: p.prefs.families,
        titles: p.prefs.titles,
        seniority: p.prefs.seniority,
        locations: p.prefs.locations,
        workplace: p.prefs.workplace,
        minSalary: p.prefs.minSalary ?? null,
        needsSponsorship: p.workAuth.needsSponsorship,
      },
      workAuth: { needsSponsorship: p.workAuth.needsSponsorship },
    }),
  },
  {
    label: "Questions",
    title: "A few common questions.",
    lede: "Most applications ask these. Answer them here and Headcount fills them in for you.",
    patch: (p) => ({
      workAuth: { authorizedToWork: p.workAuth.authorizedToWork, citizenship: p.workAuth.citizenship?.trim() },
      relocation: { willing: p.relocation.willing },
      attestations: p.attestations,
      startDate: p.startDate?.trim(),
      salaryExpectation: p.salaryExpectation?.trim(),
    }),
  },
];

/** What a step still lacks before autopilot can send anything, as words for the notice. */
function lacking(p: ProfileView, at: number): string[] {
  if (at === 1) return [];
  if (at === 2) {
    const a = p.attestations;
    return [a.convicted === undefined && "felony answer", a.formerEmployee === undefined && "worked-there-before answer"].filter((x): x is string => !!x);
  }
  const out = (["firstName", "lastName", "email", "phone"] as const).filter((k) => !p[k].trim());
  return [...out, ...(p.hasResume ? [] : ["resume" as const])].map((k) => NEEDS[k]!);
}

/** Full-screen first run: resume and contact, the jobs they want, and standing answers, then into the app. */
export function Welcome() {
  const app = useApp();
  const [, navigate] = useLocation();
  const [draft, setDraft] = useState<ProfileView | null>(null);
  const [at, setAt] = useState(() => Math.min(app.onboarding?.step ?? 0, 2));
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (!app.profile || draft) return;
    const p = { ...app.profile, email: app.profile.email || app.user.email };
    setDraft(p);
    getSession().then((s) => {
      const meta = (s?.user.user_metadata ?? {}) as { given_name?: string; family_name?: string; full_name?: string };
      const [first, ...rest] = (meta.full_name ?? "").trim().split(/\s+/);
      const found = { firstName: meta.given_name ?? first, lastName: meta.family_name ?? (rest.join(" ") || undefined) };
      setDraft((d) => d && applyFound(d, found));
    }, () => {});
  }, [app.profile, app.user.email, draft]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [at]);

  if (finishing) return <Finish onDone={() => navigate(pickedPlan() ? "/settings/billing" : "/", { replace: true })} />;
  if (app.onboarding?.done && !saving) return <Redirect to={pickedPlan() ? "/settings/billing" : "/"} replace />;
  if (!draft) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <MarkLoader delay={0.4} />
      </div>
    );
  }

  const step = STEPS[at]!;
  const missing = lacking(draft, at);
  const set = (fn: (p: ProfileView) => ProfileView) => setDraft((d) => d && fn(d));

  const skip = () => {
    try {
      sessionStorage.setItem(SKIP_KEY, "1");
    } catch {}
    navigate("/", { replace: true });
  };

  const next = async () => {
    if (missing.length) return setTried(true);
    setSaving(true);
    try {
      const p = await saveProfile({ ...step.patch(draft), onboardingStep: Math.max(at + 1, draft.onboardingStep ?? 0) });
      setDraft((d) => d && { ...d, onboardingStep: p.onboardingStep });
      app.setProfile(p);
      setTried(false);
      if (at < STEPS.length - 1) setAt(at + 1);
      else setFinishing(true);
    } catch (e) {
      app.toast(errorText(e), "bad");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-dvh bg-page pb-28">
      <header className="sticky top-0 z-10 border-b border-line bg-page/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between gap-4 px-4 md:px-6">
          <Logo size={22} />
          <Button variant="ghost" size="sm" onClick={skip}>
            Skip for now
          </Button>
        </div>
        <Rail at={at} />
      </header>
      <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 pt-8 md:px-6 md:pt-12">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={at}
            className="flex flex-col gap-6"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.22, ease }}
          >
            <div className="flex flex-col gap-2">
              <h1 className="font-display text-[30px] leading-[1.1] text-ink md:text-[36px]">{step.title}</h1>
              <p className="text-[15px] text-muted">{step.lede}</p>
            </div>
            {at === 0 ? (
              <>
                <ResumeDrop
                  name={draft.resumeName}
                  onDone={(p, f) =>
                    set((d) => {
                      const next = { ...d, resumeName: p.resumeName, hasResume: p.hasResume };
                      return f ? applyFound(f.email && d.email === app.user.email ? { ...next, email: "" } : next, f) : next;
                    })
                  }
                />
                {draft.hasResume && (
                  <Section>
                    <Fold label="Contact" summary={contactSummary(draft)} open={lacking(draft, 0).length > 0} force={tried && missing.length > 0}>
                      <ContactFields p={draft} set={set} rows={["firstName", "lastName", "pronouns", "email", "phone", "place"]} />
                    </Fold>
                    <Fold label="Links" summary={linkSummary(draft)}>
                      <ContactFields p={draft} set={set} rows={["linkedin", "github", "website"]} />
                    </Fold>
                    <Fold label="Education" summary={schoolSummary(draft)}>
                      <EducationFields p={draft} set={set} />
                    </Fold>
                  </Section>
                )}
                {draft.hasResume && <p className="text-[13px] text-muted">We only use these details to fill in your applications.</p>}
              </>
            ) : at === 1 ? (
              <Section>
                <PrefFields p={draft} set={set} rows={["families", "seniority", "workplace", ...(remoteOnly(draft) ? [] : (["locations"] as const)), "sponsorship"]} short />
                <Fold label="More filters" summary={filterSummary(draft)}>
                  <PrefFields p={draft} set={set} rows={["titles", "minSalary"]} />
                </Fold>
              </Section>
            ) : (
              <Section>
                <AnswerFields p={draft} set={set} rows={["authorized", "relocate", "convicted", "formerEmployee"]} />
                <Fold label="More answers" summary={answerSummary(draft)}>
                  <AnswerFields p={draft} set={set} rows={["startDate", "salary", "citizenship"]} />
                </Fold>
              </Section>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
      <footer className="fixed inset-x-0 bottom-0 z-10 border-t border-line bg-page/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:px-6">
          {tried && missing.length > 0 && <Notice>{at === 2 ? "Answer the felony and worked-there-before questions." : !draft.hasResume ? "Add your resume." : `Add your ${list(missing)}.`}</Notice>}
          <div className="flex items-center gap-3 md:ml-auto">
            {at > 0 && (
              <Button variant="ghost" icon={<ArrowLeft weight="bold" />} onClick={() => setAt(at - 1)} disabled={saving}>
                Back
              </Button>
            )}
            <Button size="lg" className="ml-auto" loading={saving} onClick={next}>
              {at < STEPS.length - 1 ? "Continue" : "Start applying"}
            </Button>
          </div>
        </div>
      </footer>
    </main>
  );
}

const FINISH = ["Saving your profile", "Finding jobs that match", "Getting autopilot ready"];

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** After the last step: the setup ticks through as checks while the first feed loads, then a short celebration before the app. */
function Finish({ onDone }: { onDone: () => void }) {
  const [at, setAt] = useState(1);
  const title = useRef<HTMLHeadingElement>(null);
  const done = at >= FINISH.length;

  useEffect(() => {
    let live = true;
    (async () => {
      await Promise.all([getFeed(20).catch(() => null), wait(1300)]);
      if (!live) return;
      setAt(2);
      await wait(900);
      if (!live) return;
      setAt(3);
      await wait(250);
      confettiFrom(title.current, 140);
      await wait(1500);
      if (live) onDone();
    })();
    return () => {
      live = false;
    };
  }, []);

  return (
    <main className="grid min-h-dvh place-items-center bg-page px-4">
      <motion.div className="flex w-full max-w-sm flex-col gap-8" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, ease }}>
        <Logo size={22} />
        <div className="flex flex-col gap-2">
          <AnimatePresence mode="wait" initial={false}>
            <motion.h1
              key={done ? "done" : "busy"}
              ref={title}
              className="font-display text-[30px] leading-[1.1] text-ink md:text-[36px]"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2, ease }}
            >
              {done ? "You're set." : "Setting things up."}
            </motion.h1>
          </AnimatePresence>
          <p className={cn("text-[15px] text-muted transition-opacity duration-300", !done && "opacity-0")}>Headcount starts applying.</p>
        </div>
        <Steps steps={FINISH.map((label, i) => ({ label, state: i < at ? "done" : i === at ? "active" : "todo" }))} />
      </motion.div>
    </main>
  );
}

const list = (xs: string[]) => (xs.length < 2 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

const remoteOnly = (p: ProfileView) => p.prefs.workplace.length === 1 && p.prefs.workplace[0] === "remote";

const join = (xs: (string | false | null | undefined)[]) => xs.filter(Boolean).join(" · ");

function contactSummary(p: ProfileView) {
  const place = [p.address?.city, [p.address?.state, p.address?.postal].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return join([`${p.firstName} ${p.lastName}`.trim(), p.pronouns, p.email, p.phone, place]);
}

const linkSummary = (p: ProfileView) => join([p.links.linkedin, p.links.github, p.links.website]) || "Optional";

const schoolSummary = (p: ProfileView) => join([p.education[0]?.school, p.education[0]?.degree]) || "Optional";

function filterSummary(p: ProfileView) {
  const pay = p.prefs.minSalary ? `$${num(p.prefs.minSalary)}+` : null;
  return join([p.prefs.titles.join(", "), pay]) || "Job titles and minimum pay";
}

const answerSummary = (p: ProfileView) => join([p.startDate, p.salaryExpectation, p.workAuth.citizenship]) || "Start date, expected pay and citizenship";

/** A row that shows a one-line summary while closed and its fields while open. `force` opens it, e.g. when a field inside is missing. */
function Fold({ label, summary, open: initial = false, force = false, children }: { label: string; summary: string; open?: boolean; force?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(initial);
  useEffect(() => {
    if (force) setOpen(true);
  }, [force]);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-4 px-5 py-4 text-left transition-[background-color] duration-150 hover:bg-sunk/60 md:grid md:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto] md:gap-x-8 md:px-6 md:py-5"
      >
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 md:contents">
          <span className="text-[13px] font-semibold text-ink">{label}</span>
          <span className="truncate text-[13px] text-muted">{summary}</span>
        </span>
        <CaretDown weight="bold" aria-hidden className={cn("size-4 shrink-0 text-muted transition-transform duration-200", open && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease }}
            className="overflow-hidden"
          >
            <div className="divide-y divide-line border-t border-line">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Section({ children }: { children: ReactNode }) {
  return (
    <Card className="overflow-hidden">
      <div className="divide-y divide-line">{children}</div>
    </Card>
  );
}

/** Three labelled segments under the header that fill as each step is saved. */
function Rail({ at }: { at: number }) {
  return (
    <ol className="mx-auto grid max-w-3xl grid-cols-3 gap-2 px-4 pb-3 md:px-6" aria-label="Setup progress">
      {STEPS.map((s, i) => {
        const state = i < at ? "done" : i === at ? "active" : "todo";
        return (
          <li key={s.label} className="flex flex-col gap-1.5" aria-current={state === "active" ? "step" : undefined}>
            <span className="relative h-1.5 overflow-hidden rounded-full bg-ink/10">
              <motion.span
                className={cn("absolute inset-0 origin-left rounded-full", state === "done" ? "bg-ok" : "bg-primary")}
                initial={false}
                animate={{ scaleX: state === "done" ? 1 : state === "active" ? 0.4 : 0 }}
                transition={softSpring}
              />
            </span>
            <span className={cn("flex items-center gap-1 text-[13px] font-semibold transition-[color] duration-200", state === "todo" ? "text-muted" : "text-ink")}>
              {state === "done" && <Check weight="bold" className="size-3.5 text-ok" aria-hidden />}
              {s.label}
              <span className="sr-only">{state === "done" ? ", done" : state === "active" ? ", current step" : ""}</span>
            </span>
          </li>
        );
      })}
    </ol>
  );
}

type Phase = "idle" | "upload" | "read";

/** A large drop target that uploads the resume and reads the contact details off it, reporting both once the read is done. */
function ResumeDrop({ name, onDone }: { name: string | null; onDone: (p: ProfileView, found: ReturnType<typeof findContact> | null) => void }) {
  const app = useApp();
  const input = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [over, setOver] = useState(false);

  const take = async (file: File | undefined) => {
    if (!file || phase !== "idle") return;
    setPhase("upload");
    const reading = fileText(file);
    try {
      const p = await uploadResume(file);
      app.setProfile(p);
      setPhase("read");
      const text = await reading;
      onDone(p, text ? findContact(text) : null);
    } catch (e) {
      const code = (e as Error).message;
      app.toast(code === "bad_type" ? "Use a PDF, Word, RTF, ODT or text file." : errorText(e, "The upload didn't go through. Try again."), "bad");
    } finally {
      setPhase("idle");
      if (input.current) input.current.value = "";
    }
  };

  return (
    <button
      type="button"
      onClick={() => input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        take(e.dataTransfer.files[0]);
      }}
      disabled={phase !== "idle"}
      className={cn(
        "flex min-h-36 w-full flex-col items-center justify-center gap-3 rounded-card border-2 border-dashed px-6 py-8 text-center transition-[background-color,border-color] duration-150",
        over ? "border-primary bg-primary-soft" : name ? "border-ok/40 bg-tone-mint/40" : "border-line-strong bg-surface hover:border-primary/60 hover:bg-primary-soft/40",
      )}
    >
      <input ref={input} type="file" accept={RESUME_ACCEPT} hidden onChange={(e) => take(e.target.files?.[0])} />
      {phase !== "idle" ? (
        <Steps
          steps={[
            { label: "Uploading your resume", state: phase === "upload" ? "active" : "done" },
            { label: "Reading your details", state: phase === "read" ? "active" : "todo" },
          ]}
        />
      ) : name ? (
        <>
          <Steps steps={[{ label: name, state: "done" }]} />
          <span className="text-[13px] font-semibold text-primary-ink">Replace</span>
        </>
      ) : (
        <>
          <FileArrowUp size={28} weight="duotone" className="text-primary" aria-hidden />
          <span className="text-[15px] font-semibold text-ink">Choose your resume, or drop it here.</span>
          <span className="text-[13px] text-muted">PDF, Word or text.</span>
        </>
      )}
    </button>
  );
}
